import { McpTokenAccess } from "./McpTokenAccess";
import type { FC } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Label, TextInput } from "flowbite-react";
import {
  HiOutlineCheck,
  HiOutlineClipboardCopy,
  HiOutlinePlay,
  HiOutlinePlus,
  HiOutlineTerminal,
  HiTrash,
  HiX,
} from "react-icons/hi";
import {
  Dialog,
  DialogForm,
  HelpLabel,
  InfoMessageAtom,
  StandardForm,
  StandardTable,
  StatusBadge,
  TableSkeleton,
} from "@components";
import { useUserSession } from "../../../context/UserContext";
import { useWorkspace } from "../../../context/WorkspaceContext";
import {
  createMcpToken,
  getMcpTokens,
  McpTokenCredentials,
  McpTokenMetadata,
  revokeMcpToken,
} from "../../../api";
import { ITableRow } from "../../../types";
import { SettingsRowProps } from "../components/SettingsRow";

interface McpTokensSettingsContentProps {
  SettingsRow: React.ComponentType<SettingsRowProps>;
}

interface McpTokenCreateData {
  name: string;
  scopes?: string | string[];
}

const MCP_URL = process.env.REACT_APP_MCP_URL || "https://mcp-dev.logibud.com/mcp";
const MCP_CLAUDE_HELP_VIDEO_URL =
  process.env.REACT_APP_MCP_CLAUDE_HELP_VIDEO_URL || "https://www.youtube.com/watch?v=MX_4dM6JUYI";

const parseDate = (value?: any): Date | null => {
  if (!value) return null;
  if (typeof value === "string") {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  if (typeof value.seconds === "number") {
    return new Date(value.seconds * 1000);
  }
  return null;
};

const formatDate = (value?: any, emptyValue = "-"): string => {
  const date = parseDate(value);
  if (!date) return emptyValue;
  return date.toLocaleDateString();
};

const shortHash = (value?: string): string => {
  if (!value) return "-";
  return `${value.slice(0, 8)}...${value.slice(-6)}`;
};

const normalizeScopes = (scopes?: string | string[]): string[] => {
  if (!scopes) return [];
  return Array.isArray(scopes) ? scopes : [scopes];
};

const getDisplayScopes = (scopes?: string[]): string[] => {
  return scopes && scopes.length > 0 ? scopes : ["drivers:read"];
};

const MCP_SCOPE_RESOURCES = [
  "drivers",
  "customers",
  "companies",
  "products",
  "vehicles",
  "locations",
  "orders",
  "routes",
] as const;
const MCP_SCOPE_ACTIONS = ["read", "create", "update", "optimize"] as const;
const ENABLED_MCP_SCOPES = new Set([
  ...MCP_SCOPE_RESOURCES.map((resource) => `${resource}:read`),
  "customers:create",
  "customers:update",
  "drivers:create",
  "locations:create",
  "orders:create",
  "routes:update",
  "routes:optimize",
  "vehicles:create",
]);

const getYoutubeVideoId = (url: string): string | null => {
  if (!url) return null;
  try {
    const parsed = new URL(url);
    if (parsed.hostname.includes("youtu.be")) {
      return parsed.pathname.split("/").filter(Boolean)[0] || null;
    }
    if (parsed.hostname.includes("youtube.com")) {
      return parsed.searchParams.get("v");
    }
  } catch {
    return null;
  }
  return null;
};

const getYoutubeEmbedUrl = (videoId: string | null): string | null => {
  return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
};

const CopyButton: FC<{ value: string; label?: string; className?: string }> = ({ value, label, className }) => {
  const { t } = useTranslation("common");
  const [copied, setCopied] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const handleCopy = () => {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      buttonRef.current?.blur();
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <button
      ref={buttonRef}
      type="button"
      onClick={handleCopy}
      aria-label={label || t("copy_to_clipboard", "Copy")}
      className={className || `flex h-10 w-10 items-center justify-center rounded-lg border transition-colors ${
        copied
          ? "border-green-500 bg-green-500 text-white"
          : "border-gray-300 bg-gray-100 text-gray-700 hover:bg-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
      }`}
    >
      {copied ? <HiOutlineCheck className="h-5 w-5" /> : <HiOutlineClipboardCopy className="h-5 w-5" />}
      {label ? <span className="ml-2">{copied ? t("copied", "Copied") : label}</span> : null}
    </button>
  );
};

const CopyableField: FC<{ label: string; value: string; description?: string; secret?: boolean }> = ({
  label,
  value,
  description,
  secret,
}) => {
  return (
    <div>
      <div className="mb-1 block">
        <Label value={label} className="text-sm font-semibold text-gray-700 dark:text-gray-300" />
      </div>
      <div className="flex items-center gap-2">
        <TextInput
          value={value}
          readOnly
          type={secret ? "password" : "text"}
          className="min-w-0 flex-1 font-mono text-xs"
        />
        <CopyButton value={value} />
      </div>
      {description ? (
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{description}</p>
      ) : null}
    </div>
  );
};

const McpTokenCreateForm: FC<{
  onConfirm: (data: McpTokenCreateData) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
}> = ({ onConfirm, onCancel, isSubmitting }) => {
  const { t } = useTranslation(["common", "workspace", "user"]);
  const [scopeError, setScopeError] = useState(false);

  const handleConfirm = (data: McpTokenCreateData) => {
    if (normalizeScopes(data.scopes).length === 0) {
      setScopeError(true);
      return;
    }
    setScopeError(false);
    onConfirm(data);
  };

  return (
    <StandardForm
      showHeader={false}
      fields={[
        {
          id: "name",
          label: t("common:name"),
          input: (
            <TextInput
              id="name"
              key="name"
              name="name"
              placeholder={t("workspace:mcp_tokens.name_placeholder")}
              type="text"
              required
            />
          ),
        },
        {
          id: "scopes",
          label: t("workspace:mcp_tokens.access_label"),
          grid_style: "col-span-full",
          input: (
            <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
              <table className="w-full table-fixed text-left text-sm">
                <thead className="bg-gray-50 text-xs font-semibold uppercase text-gray-500 dark:bg-gray-800 dark:text-gray-400">
                  <tr>
                    <th className="w-1/3 px-3 py-2">
                      {t("workspace:mcp_tokens.scope_matrix.resource")}
                    </th>
                    {MCP_SCOPE_ACTIONS.map((action) => (
                      <th key={action} className="px-3 py-2 text-center">
                        {t(`workspace:mcp_tokens.scope_matrix.actions.${action}`)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {MCP_SCOPE_RESOURCES.map((resource) => (
                    <tr key={resource} className="text-gray-700 dark:text-gray-200">
                      <th className="px-3 py-3 font-medium">
                        {t(`workspace:mcp_tokens.scope_matrix.resources.${resource}`)}
                      </th>
                      {MCP_SCOPE_ACTIONS.map((action) => {
                        const scope = `${resource}:${action}`;
                        const isEnabled = ENABLED_MCP_SCOPES.has(scope);
                        return (
                          <td key={scope} className="p-0 text-center">
                            <label
                              className={`flex min-h-[44px] w-full items-center justify-center px-3 py-3 ${
                                isEnabled
                                  ? "cursor-pointer hover:bg-lgb-blue-50 dark:hover:bg-lgb-blue-400/10"
                                  : "cursor-not-allowed"
                              }`}
                              title={!isEnabled ? t("workspace:mcp_tokens.scope_matrix.unavailable") : undefined}
                            >
                              <input
                                type="checkbox"
                                name="scopes"
                                value={scope}
                                defaultChecked={scope === "drivers:read"}
                                disabled={!isEnabled}
                                className="h-4 w-4 cursor-pointer rounded border-gray-300 text-lgb-blue-300 focus:ring-lgb-blue-300 disabled:cursor-not-allowed disabled:opacity-40"
                              />
                            </label>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
              {scopeError ? (
                <p className="border-t border-gray-200 px-3 py-2 text-sm text-lgb-red-400 dark:border-gray-700">
                  {t("workspace:mcp_tokens.scope_required")}
                </p>
              ) : null}
              <p className="border-t border-gray-200 px-3 py-2 text-xs leading-5 text-gray-500 dark:border-gray-700 dark:text-gray-400">
                {t("workspace:mcp_tokens.create_scope_warning")}
              </p>
            </div>
          ),
        },
      ]}
      onConfirm={handleConfirm}
      onCancel={onCancel}
      isSubmitting={isSubmitting}
      buttonConfirmText={t("workspace:mcp_tokens.create_button")}
      buttonConfirmPrependIcon={<HiOutlinePlus className="mr-2 h-4 w-4" />}
      buttonCloseText={t("common:cancel")}
    />
  );
};

const McpTokenHelpContent: FC = () => {
  const { t } = useTranslation("workspace");

  return (
    <div className="space-y-4 text-sm text-gray-700 dark:text-gray-200">
      <div>
        <h4 className="text-base font-semibold text-gray-900 dark:text-white">
          {t("mcp_tokens.help.what_title")}
        </h4>
        <p className="mt-1">{t("mcp_tokens.help.what_description")}</p>
      </div>
      <div>
        <h4 className="text-base font-semibold text-gray-900 dark:text-white">
          {t("mcp_tokens.help.how_title")}
        </h4>
        <p className="mt-1">{t("mcp_tokens.help.how_description")}</p>
      </div>
      <div>
        <h4 className="text-base font-semibold text-gray-900 dark:text-white">
          {t("mcp_tokens.help.security_title")}
        </h4>
        <p className="mt-1">{t("mcp_tokens.help.security_description")}</p>
      </div>
    </div>
  );
};

const McpVideoModal: FC<{
  show: boolean;
  title: string;
  embedUrl: string | null;
  onClose: () => void;
}> = ({ show, title, embedUrl, onClose }) => {
  useEffect(() => {
    const body = document.getElementsByTagName("body")[0];
    if (show) {
      body.style.overflow = "hidden";
      return () => {
        body.style.overflow = "";
      };
    }
    body.style.overflow = "";
  }, [show]);

  if (!show || !embedUrl) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className="fixed inset-0 z-50 flex items-center justify-center bg-gray-950/85 p-3 sm:p-6"
      onClick={onClose}
    >
      <div
        className="w-full max-w-6xl overflow-hidden rounded-xl bg-gray-950 text-white shadow-2xl ring-1 ring-white/10"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-white/10 px-5 py-4">
          <div>
            <h3 className="text-base font-semibold text-white">{title}</h3>
            <p className="mt-1 text-sm text-gray-300">
              Claude MCP setup
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex h-9 w-9 items-center justify-center rounded-full text-gray-300 transition-colors hover:bg-white/10 hover:text-white"
          >
            <HiX className="h-5 w-5" />
          </button>
        </div>
        <div className="bg-black p-0 sm:p-4">
          <div className="aspect-video w-full overflow-hidden bg-black sm:rounded-lg">
            <iframe
              title={title}
              src={embedUrl}
              className="h-full w-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const McpVideoGuide: FC = () => {
  const { t } = useTranslation("workspace");
  const [isVideoOpen, setVideoOpen] = useState(false);
  const youtubeVideoId = getYoutubeVideoId(MCP_CLAUDE_HELP_VIDEO_URL);
  const embedUrl = getYoutubeEmbedUrl(youtubeVideoId);
  const thumbnailUrl = youtubeVideoId ? `https://img.youtube.com/vi/${youtubeVideoId}/hqdefault.jpg` : null;
  const openVideo = () => {
    if (embedUrl) setVideoOpen(true);
  };

  return (
    <>
      <section className="rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 items-center gap-3">
            <button
              type="button"
              onClick={openVideo}
              disabled={!embedUrl}
              className={`relative flex h-16 w-28 shrink-0 items-center justify-center overflow-hidden rounded-md border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900 ${
                embedUrl ? "cursor-pointer" : "cursor-not-allowed"
              }`}
            >
              {thumbnailUrl ? (
                <img
                  src={thumbnailUrl}
                  alt=""
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center bg-gray-100 text-gray-400 dark:bg-gray-900">
                  <HiOutlinePlay className="h-7 w-7" />
                </div>
              )}
              <span className="absolute inset-0 flex items-center justify-center bg-black/20">
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white/90 text-lgb-blue-300 shadow">
                  <HiOutlinePlay className="h-4 w-4" />
                </span>
              </span>
            </button>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-gray-900 dark:text-white">
                {t("mcp_tokens.video_guide.title")}
              </p>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                {t(embedUrl ? "mcp_tokens.video_guide.description" : "mcp_tokens.video_guide.placeholder")}
              </p>
            </div>
          </div>
          <Button
            color="light"
            onClick={openVideo}
            disabled={!embedUrl}
            className="shrink-0"
          >
            <HiOutlinePlay className="mr-2 h-4 w-4" />
            {t(embedUrl ? "mcp_tokens.video_guide.open_button" : "mcp_tokens.video_guide.coming_soon")}
          </Button>
        </div>
      </section>
      <McpVideoModal
        title={t("mcp_tokens.video_guide.modal_title")}
        show={isVideoOpen}
        embedUrl={embedUrl}
        onClose={() => setVideoOpen(false)}
      />
    </>
  );
};

const McpTokenCredentialsModal: FC<{
  token: McpTokenCredentials | null;
  onClose: () => void;
}> = ({ token, onClose }) => {
  const { t } = useTranslation(["common", "workspace", "user"]);

  if (!token) return null;

  const technicalConfig = JSON.stringify(
    {
      url: MCP_URL,
      authorization: `Bearer ${token.mcp_token}`,
    },
    null,
    2,
  );

  return (
    <Dialog
      title={t("workspace:mcp_tokens.created_title")}
      show={Boolean(token)}
      toggleModal={onClose}
      confirmButton={onClose}
      buttonConfirmText={t("common:done", "Done")}
      buttonCloseText={t("common:close")}
      size="lg"
      content={
        <div className="space-y-4 pt-2">
          <InfoMessageAtom
            variant="warning"
            title={t("workspace:mcp_tokens.secret_warning_title")}
          >
            {t("workspace:mcp_tokens.secret_warning_message")}
          </InfoMessageAtom>
          <CopyableField
            label={t("workspace:mcp_tokens.access_token")}
            value={token.mcp_token}
            description={t("workspace:mcp_tokens.access_token_help")}
            secret
          />
          <div className="flex flex-col gap-2 rounded-lg border border-dashed border-gray-300 p-3 dark:border-gray-600 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-sm font-medium text-gray-800 dark:text-gray-100">
                {t("workspace:mcp_tokens.technical_config_title")}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {t("workspace:mcp_tokens.technical_config_description")}
              </p>
            </div>
            <CopyButton
              value={technicalConfig}
              label={t("workspace:mcp_tokens.copy_technical_config")}
              className="inline-flex h-10 items-center justify-center rounded-lg border border-gray-300 bg-white px-3 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
            />
          </div>
        </div>
      }
    />
  );
};

const McpTokensSettingsContent: FC<McpTokensSettingsContentProps> = () => {
  const { t } = useTranslation(["common", "workspace"]);
  const { activeWorkspace } = useWorkspace();
  const { userSession } = useUserSession();
  const [isLoading, setIsLoading] = useState(true);
  const [tokens, setTokens] = useState<McpTokenMetadata[]>([]);
  const [showRevokedTokens, setShowRevokedTokens] = useState(false);
  const [isCreateModalOpen, setCreateModalOpen] = useState(false);
  const [isCreatingToken, setCreatingToken] = useState(false);
  const [createdToken, setCreatedToken] = useState<McpTokenCredentials | null>(null);
  const [tokenToRevoke, setTokenToRevoke] = useState<McpTokenMetadata | null>(null);

  const loadTokens = useCallback(async () => {
    if (!userSession?.token?.accessToken || !activeWorkspace?.workspaceId) {
      setTokens([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const result = await getMcpTokens(userSession.token.accessToken, activeWorkspace.workspaceId);
      setTokens(
        [...result].sort((a, b) => {
          const aTime = a.createdAt?.seconds ? a.createdAt.seconds * 1000 : Date.parse(a.createdAt ?? "");
          const bTime = b.createdAt?.seconds ? b.createdAt.seconds * 1000 : Date.parse(b.createdAt ?? "");
          return (Number.isNaN(bTime) ? 0 : bTime) - (Number.isNaN(aTime) ? 0 : aTime);
        }),
      );
    } finally {
      setIsLoading(false);
    }
  }, [activeWorkspace?.workspaceId, userSession?.token?.accessToken]);

  useEffect(() => {
    loadTokens();
  }, [loadTokens]);

  const handleCreate = async (data: McpTokenCreateData) => {
    if (!userSession?.token?.accessToken || !activeWorkspace?.workspaceId) return;

    const scopes = normalizeScopes(data.scopes);
    if (scopes.length === 0) return;

    setCreatingToken(true);
    try {
      const token = await createMcpToken(
        userSession.token.accessToken,
        activeWorkspace.workspaceId,
        data.name,
        "ReadOnly",
        scopes,
      );
      if (!token) return;

      setCreatedToken(token);
      setCreateModalOpen(false);
      await loadTokens();
    } finally {
      setCreatingToken(false);
    }
  };

  const handleRevoke = async () => {
    if (!tokenToRevoke || !userSession?.token?.accessToken || !activeWorkspace?.workspaceId) return;

    await revokeMcpToken(
      userSession.token.accessToken,
      activeWorkspace.workspaceId,
      tokenToRevoke.token_hash,
    );
    setTokenToRevoke(null);
    await loadTokens();
  };

  const visibleTokens = useMemo(
    () => tokens.filter((token) => showRevokedTokens || !token.revokedAt),
    [showRevokedTokens, tokens],
  );

  const revokedTokenCount = useMemo(
    () => tokens.filter((token) => Boolean(token.revokedAt)).length,
    [tokens],
  );

  const tableRows = useMemo<ITableRow[]>(
    () =>
      visibleTokens.map((token) => {
        const isRevoked = Boolean(token.revokedAt);
        const scopes = getDisplayScopes(token.scopes);
        return {
          id: token.token_hash,
          cells: [
            {
              id: "name",
              children: token.name || shortHash(token.token_hash),
              showOnSmallScreen: true,
            },
            {
              id: "access",
              children: (
                <McpTokenAccess scopes={scopes} name={token.name || shortHash(token.token_hash)} />
              ),
            },
            {
              id: "status",
              children: (
                <StatusBadge
                  status={isRevoked ? t("workspace:mcp_tokens.revoked") : t("workspace:mcp_tokens.active")}
                  bgStyle={
                    isRevoked
                      ? "bg-lgb-red-100 dark:bg-lgb-red-200 text-lgb-red-400"
                      : "bg-lgb-green-100 dark:bg-lgb-green-200 text-lgb-green-400 dark:text-lgb-green-400"
                  }
                  dotStyle={isRevoked ? "bg-lgb-red-400" : "bg-lgb-green-400"}
                />
              ),
            },
            {
              id: "lastUsedAt",
              children: formatDate(token.lastUsedAt, t("workspace:mcp_tokens.never_used")),
            },
            {
              id: "createdAt",
              children: formatDate(token.createdAt),
            },
          ],
          actions: [
            {
              id: "revoke",
              icon: <HiTrash />,
              color: "failure",
              onActionClick: () => setTokenToRevoke(token),
            },
          ].filter(() => !isRevoked),
        };
      }),
    [t, visibleTokens],
  );

  const tableHeader = [
    {
      id: "name",
      children: t("common:name"),
      showOnSmallScreen: true,
    },
    {
      id: "access",
      children: t("workspace:mcp_tokens.access_column"),
    },
    {
      id: "status",
      children: t("workspace:mcp_tokens.status"),
    },
    {
      id: "lastUsedAt",
      children: t("workspace:mcp_tokens.last_used"),
    },
    {
      id: "createdAt",
      children: t("common:created_at"),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col items-start justify-between gap-3 sm:flex-row">
        <div>
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white sm:text-2xl">
              {t("workspace:mcp_tokens.title")}
            </h2>
            <HelpLabel
              title={t("workspace:mcp_tokens.help_label")}
              content={<McpTokenHelpContent />}
              className="mt-1"
            />
          </div>
          <p className="max-w-3xl pt-2 text-base text-lgb-grey-600 dark:text-lgb-grey-200">
            {t("workspace:mcp_tokens.description")}
          </p>
        </div>
        <Button
          color="light"
          onClick={() => setCreateModalOpen(true)}
          type="button"
          className="mt-2 sm:mt-0"
        >
          <HiOutlinePlus className="mr-2 h-5 w-5" />
          {t("workspace:mcp_tokens.new_button")}
        </Button>
      </div>

      <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 dark:border-gray-700 dark:bg-gray-800">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <div className="flex shrink-0 items-center">
            <span className="text-sm font-semibold text-gray-800 dark:text-gray-100">
              {t("workspace:mcp_tokens.mcp_url")}
            </span>
          </div>
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <code className="min-w-0 flex-1 truncate rounded bg-white px-2 py-1.5 font-mono text-sm text-gray-700 dark:bg-gray-900 dark:text-gray-200">
              {MCP_URL}
            </code>
            <CopyButton value={MCP_URL} />
          </div>
        </div>
      </div>

      <McpVideoGuide />

      {revokedTokenCount > 0 ? (
        <label className="flex w-fit cursor-pointer items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
          <input
            type="checkbox"
            checked={showRevokedTokens}
            onChange={(event) => setShowRevokedTokens(event.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          {t("workspace:mcp_tokens.show_revoked", { count: revokedTokenCount })}
        </label>
      ) : null}

      {isLoading ? (
        <TableSkeleton />
      ) : tokens.length === 0 ? (
        <div className="py-8 text-center">
          <HiOutlineTerminal className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">
            {t("workspace:mcp_tokens.empty_state.title")}
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {t("workspace:mcp_tokens.empty_state.description")}
          </p>
        </div>
      ) : visibleTokens.length === 0 ? (
        <div className="py-8 text-center">
          <HiOutlineTerminal className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">
            {t("workspace:mcp_tokens.empty_active_state.title")}
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {t("workspace:mcp_tokens.empty_active_state.description")}
          </p>
        </div>
      ) : (
        <StandardTable header={tableHeader} rows={tableRows} hasActions compact />
      )}

      <Dialog
        title={t("workspace:mcp_tokens.create_title")}
        content={
          <McpTokenCreateForm
            onConfirm={handleCreate}
            onCancel={() => setCreateModalOpen(false)}
            isSubmitting={isCreatingToken}
          />
        }
        show={isCreateModalOpen}
        toggleModal={() => setCreateModalOpen(false)}
        hideButtons
      />

      <McpTokenCredentialsModal
        token={createdToken}
        onClose={() => setCreatedToken(null)}
      />

      <DialogForm
        title={t("workspace:mcp_tokens.revoke_title", { name: tokenToRevoke?.name || shortHash(tokenToRevoke?.token_hash) })}
        show={Boolean(tokenToRevoke)}
        toggleModal={() => setTokenToRevoke(null)}
        showHeader={false}
        buttonConfirmColor="failure"
        dismissible
        confirmButton={handleRevoke}
        buttonConfirmText={t("workspace:mcp_tokens.revoke_button")}
        buttonConfirmPrependIcon={<HiTrash className="mr-2 h-4 w-4" />}
        buttonCloseText={t("common:close")}
      />
    </div>
  );
};

export default McpTokensSettingsContent;
